import { calcBauteil, calcPos, type FotoRef } from '@wertermittlung/shared';
import { api } from '../../lib/api.js';
import { fmt, numFormat } from '../../lib/dom.js';
import { summen } from '../../lib/state.js';
import type { WizardCtx } from './types.js';

const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"]/g,
    (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[m]!,
  );

/** Baut die Druckansicht (nur erfasste Positionen) und öffnet den Druckdialog. */
export function druckeProtokoll(ctx: WizardCtx): void {
  const root = document.getElementById('printRoot');
  if (!root) return;
  root.innerHTML = protokollHtml(ctx);
  const bilder = Array.from(root.querySelectorAll('img'));
  void Promise.allSettled(
    bilder.map(
      (img) =>
        new Promise<void>((res) => {
          if (img.complete) return res();
          img.onload = img.onerror = () => res();
        }),
    ),
  ).then(() => window.print());
}

function protokollHtml(ctx: WizardCtx): string {
  const d = ctx.p.data;
  const s = summen(ctx.p);
  const dat = d.grund.datum
    ? new Date(`${d.grund.datum}T12:00`).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : '—';

  let h = `<h1>WERTERMITTLUNGSPROTOKOLL</h1>
  <div class="meta">Wertermittlung von Anpflanzungen und Anlagen nach § 11 Abs. 1 Bundeskleingartengesetz (BKleingG) ·
  Richtwertkatalog des Landesverbandes Schleswig-Holstein der Gartenfreunde e. V. · Anpassungsfaktor ${ctx.p.jahr}: ${numFormat(ctx.p.faktor)}</div>
  <table><tbody>
    <tr><th>Kleingartenanlage</th><td>${esc(d.grund.anlage) || '—'}</td><th>Parzellen-Nr.</th><td>${esc(d.grund.parzelle) || '—'}</td></tr>
    <tr><th>Fläche</th><td>${d.grund.flaeche ? `${numFormat(d.grund.flaeche)} m²` : '—'}</td><th>Datum der Begehung</th><td>${dat}</td></tr>
    <tr><th>Pächter/in</th><td>${esc(d.grund.paechter) || '—'}</td><th>Anschrift</th><td>${esc([d.grund.strasse, d.grund.ort].filter(Boolean).join(', ')) || '—'}</td></tr>
    <tr><th>Anwesend</th><td colspan="3">${esc(d.grund.anwesend) || '—'}</td></tr>
  </tbody></table>`;

  const bauRows = d.bauteile.filter((b) => (b.flaeche ?? 0) > 0);
  if (bauRows.length) {
    h += `<h2>Tabelle 1 – Bewertung der Baulichkeiten</h2>
    <table><thead><tr><th>Bauteil</th><th>Ausführung / Zustand</th><th class="r">Richtwert</th><th class="r">Fläche</th><th class="r">Abschreibung</th><th class="r">Zeitwert</th></tr></thead><tbody>`;
    for (const b of bauRows) {
      const e = calcBauteil(b, ctx.p.faktor, ctx.p.jahr);
      const zustand = ctx.katalog.zustand[b.zustand]?.label.split('–')[0]?.trim() ?? '';
      h += `<tr><td>${esc(b.typ)}${b.baujahr ? ` (Bj. ${b.baujahr})` : ''}</td><td>${esc([b.ausfuehrung, zustand].filter(Boolean).join(' · '))}</td>
      <td class="r">${numFormat(b.rw)} €/m²</td><td class="r">${numFormat(b.flaeche ?? 0)} m²</td>
      <td class="r">${numFormat(e.abschreibung)} %</td><td class="r">${fmt(e.zeitwert)}</td></tr>`;
    }
    h += `<tr><td colspan="5" style="text-align:right"><b>Gesamtsumme Baulichkeiten</b></td><td class="r"><b>${fmt(s.bau)}</b></td></tr></tbody></table>`;
  }

  const tab = (titel: string, rows: typeof d.pos.neben, extra: string): string => {
    if (!rows.length) return '';
    let t = `<h2>${titel}</h2><table><thead><tr><th>Position</th><th class="r">Menge</th><th>Bewertungskriterien</th><th class="r">Richtwert</th><th class="r">Wertmind.</th><th class="r">Endpreis</th></tr></thead><tbody>`;
    for (const p of rows) {
      t += `<tr><td>${esc(p.name)}</td><td class="r">${numFormat(p.menge)} ${esc(p.einheit)}</td><td>${esc(p.krit)}</td>
      <td class="r">${numFormat(p.preis)} €</td><td class="r">${p.minderung} %</td><td class="r">${fmt(calcPos(p, ctx.p.faktor))}</td></tr>`;
    }
    return t + extra + '</tbody></table>';
  };
  const rechts = (text: string, wert: string, fett = false) =>
    `<tr><td colspan="5" style="text-align:right">${fett ? `<b>${text}</b>` : text}</td><td class="r">${fett ? `<b>${wert}</b>` : wert}</td></tr>`;

  h += tab(
    'Tabelle 2 – Bewertung der Nebenanlagen',
    d.pos.neben,
    rechts('Gesamtsumme Nebenanlagen', fmt(s.neben), true),
  );
  h += tab(
    'Tabelle 3 – Bewertung der gärtnerischen Kulturen',
    d.pos.kulturen,
    rechts('Zwischensumme', fmt(s.kulturenZwischen)) +
      rechts(`Minderung aufgrund von Pflegerückständen ${d.pflege.kulturen} %`, '') +
      rechts('Gesamtsumme gärtnerische Kulturen', fmt(s.kulturen), true),
  );
  const capInfo = s.gedeckelt
    .map(
      (c) =>
        `<tr><td colspan="6">Hinweis: ${esc(c.label)} auf Höchstwert ${fmt(c.max)} gedeckelt (erfasst ${fmt(c.ist)}).</td></tr>`,
    )
    .join('');
  h += tab(
    'Tabelle 4 – Bewertung der Zierbegrünung',
    d.pos.zier,
    capInfo +
      rechts('Zwischensumme (nach Höchstwerten)', fmt(s.zierZwischen)) +
      rechts(`Minderung aufgrund von Pflegerückständen ${d.pflege.zier} %`, '') +
      rechts('Gesamtsumme Zierbegrünung', fmt(s.zier), true),
  );

  h += `<h2>Ergebnis der Wertermittlung</h2>`;
  if (d.nb.length) {
    h += `<p><b>Nicht bewertete Anlagen:</b></p><ul>${d.nb.map((r) => `<li>${esc(r.text)}${r.grund ? ` – ${esc(r.grund)}` : ''}</li>`).join('')}</ul>`;
  }
  if (d.abz.length) {
    h += `<p><b>Auflagen / Abzüge für Beseitigung:</b></p><ul>${d.abz.map((r) => `<li>${esc(r.text)} (${fmt(r.betrag)})</li>`).join('')}</ul>`;
  }
  if (d.auflagen) h += `<p>${esc(d.auflagen)}</p>`;
  h += `<div class="sumbox">
    <div class="row"><span>Gesamtsumme Tabelle 1 (Baulichkeiten)</span><span>${fmt(s.bau)}</span></div>
    <div class="row"><span>Gesamtsumme Tabelle 2 (Nebenanlagen)</span><span>${fmt(s.neben)}</span></div>
    <div class="row"><span>Gesamtsumme Tabelle 3 (Gärtnerische Kulturen)</span><span>${fmt(s.kulturen)}</span></div>
    <div class="row"><span>Gesamtsumme Tabelle 4 (Zierbegrünung)</span><span>${fmt(s.zier)}</span></div>
    <div class="row"><span>Leistungen für die Erstellung von Gemeinschaftseinrichtungen</span><span>${fmt(s.gemeinschaft)}</span></div>
    <div class="row"><span>Abzug für Beseitigung von Anpflanzungen und Anlagen</span><span>−${fmt(s.abzuege)}</span></div>
    <div class="row big"><span>Entschädigung insgesamt</span><span>${fmt(s.entschaedigung)}</span></div>
  </div>
  <p style="font-size:9.5px">Sämtliche baulichen Anlagen wurden entsprechend ihrem äußeren Zustand bewertet, ohne Gewähr für verdeckte Mängel und Fehler.</p>`;

  const sigs = d.sigs.filter((x) => x.img || x.name);
  if (sigs.length) {
    h += `<div class="sigrow">${sigs
      .map(
        (
          x,
        ) => `<div class="sigcell">${x.img ? `<img src="${x.img}" alt="Unterschrift">` : '<div style="height:52px"></div>'}
        <div class="line">${esc(x.name) || '&nbsp;'} · ${esc(x.funktion)}</div></div>`,
      )
      .join('')}</div>`;
  }

  const alleFotos: { ref: FotoRef; kontext: string }[] = [
    ...d.fotosAllgemein.map((ref) => ({ ref, kontext: 'Gesamteindruck' })),
    ...d.bauteile.flatMap((b) => b.fotos.map((ref) => ({ ref, kontext: b.typ }))),
    ...(['neben', 'kulturen', 'zier'] as const).flatMap((k) =>
      d.pos[k].flatMap((p) => p.fotos.map((ref) => ({ ref, kontext: p.name }))),
    ),
  ];
  if (alleFotos.length) {
    h += `<h2 class="pagebreak">Fotoanhang (${alleFotos.length} Aufnahmen, ${dat})</h2><div class="fotos">${alleFotos
      .map(
        (f, i) =>
          `<figure><img src="${api.fotoUrl(ctx.p.id, f.ref.id)}" alt=""><figcaption>Foto ${i + 1}: ${esc(f.ref.caption || f.kontext)}</figcaption></figure>`,
      )
      .join('')}</div>`;
  }
  return h;
}
